import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/group/members';

const GroupMemberSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  role: z.enum(['admin', 'member']),
});

const GroupMembersResponseSchema = z.object({ members: z.array(GroupMemberSchema) });

export type GroupMember = z.infer<typeof GroupMemberSchema>;
export type GroupMembers = GroupMember[];

export async function loadGroupMembersQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = GroupMembersResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupMembers');
    throw new SilentError();
  }

  return parseResult.data.members;
}

const noGroupMembers: GroupMembers = [];

export function useLoadGroupMembers(options: QueryOptions<GroupMembers> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<GroupMembers, ApiError>({
    queryKey: ['groupMembers'],
    queryFn: () => loadGroupMembersQueryFn(axiosInstance),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return noGroupMembers;
    },
    ...options,
  });

  return addResourceNameToQueryResult<GroupMembers, ApiError, 'groupMembers', 'PreventUndefined'>(
    'groupMembers',
    queryResult,
    noGroupMembers,
  );
}
